import type {
  AiMagicQuestion,
  AiMagicQuestionSet,
} from '../../services/ai/interview/aiMagicQuestionSet'
import type { AttachmentApprovedItem } from '../../services/ai/attachment/attachmentPlanningIntelligence'

export type InterviewAnswers = Record<string, string | string[] | undefined>

export interface InterviewSubmission {
  /** Chat-history-friendly sentence — used as the visible user-turn text. */
  humanSentence: string
  /** Deterministic JSON payload the assistant reads on the next turn. */
  structured: {
    setId: string
    answers: Array<{
      id: string
      prompt: string
      type: AiMagicQuestion['type']
      raw: string
      values?: string[]
    }>
    /** Present only for the client-authored attachment curation question. */
    attachmentApprovedItems?: AttachmentApprovedItem[]
  }
}

const stringifyValue = (question: AiMagicQuestion, answer: string | string[] | undefined): string => {
  if (Array.isArray(answer)) {
    if (question.type === 'ATTACHMENT_INVENTORY') {
      const labels = new Map(question.attachmentInventory?.sources.flatMap((source) => (
        source.items.map((item) => [item.id, item.label] as const)
      )) ?? [])
      return answer.map((value) => labels.get(value) ?? value).filter(Boolean).join(', ')
    }
    const labels = answer.map((value) => {
      const opt = question.options?.find((option) => option.value === value)
      return opt?.label ?? value
    })
    return labels.filter(Boolean).join(', ')
  }
  if (typeof answer === 'string') {
    const trimmed = answer.trim()
    if (!trimmed) return ''
    const opt = question.options?.find((option) => option.value === trimmed)
    return opt?.label ?? trimmed
  }
  return ''
}

/**
 * Build the submission payload from a QuestionSet + user answers.
 * Skips questions with no answer so the chat sentence stays clean.
 */
export const buildInterviewSubmission = (
  set: AiMagicQuestionSet,
  answers: InterviewAnswers,
): InterviewSubmission => {
  const structuredAnswers: InterviewSubmission['structured']['answers'] = []
  const attachmentApprovedItems: AttachmentApprovedItem[] = []
  const parts: string[] = []

  for (const question of set.questions) {
    const raw = answers[question.id]
    const display = stringifyValue(question, raw)
    const isAttachmentInventory = question.type === 'ATTACHMENT_INVENTORY'
    if (!display && !isAttachmentInventory) continue
    const values = Array.isArray(raw)
      ? raw.slice()
      : typeof raw === 'string' && raw.trim().length > 0
        ? [raw.trim()]
        : undefined
    const entry: InterviewSubmission['structured']['answers'][number] = {
      id: question.id,
      prompt: question.prompt,
      type: question.type,
      raw: display || '(none)',
    }
    if (values) entry.values = values
    structuredAnswers.push(entry)
    if (display) parts.push(`**${question.prompt.replace(/[?:.]+$/, '')}:** ${display}`)
    if (question.type === 'ATTACHMENT_INVENTORY' && Array.isArray(raw)) {
      const selectedIds = new Set(raw)
      question.attachmentInventory?.sources.forEach((source) => {
        source.items.forEach((item) => {
          if (!selectedIds.has(item.id)) return
          attachmentApprovedItems.push({ ...item, source: source.source })
        })
      })
      if (!display) parts.push('No attachment-derived items kept')
    }
  }

  return {
    humanSentence: parts.join(' · ') || '(No answers provided.)',
    structured: {
      setId: set.id,
      answers: structuredAnswers,
      ...(set.questions.some((question) => question.type === 'ATTACHMENT_INVENTORY')
        ? { attachmentApprovedItems }
        : {}),
    },
  }
}

export const INTERVIEW_ENVELOPE_STRUCTURED_KEY = 'structured'

/**
 * Wrap the submission into the LINE_CLARIFICATION_RESPONSE envelope, adding a
 * structured JSON line the assistant can parse deterministically. Falls back
 * to the plain envelope shape when no structured payload is present.
 */
export const buildInterviewEnvelope = (
  field: string,
  submission: InterviewSubmission,
): string => {
  return [
    '[LINE_CLARIFICATION_RESPONSE]',
    `field: ${field}`,
    `value: ${submission.humanSentence}`,
    `${INTERVIEW_ENVELOPE_STRUCTURED_KEY}: ${JSON.stringify(submission.structured)}`,
    "Treat this value as the user's explicit answer and rerun the original intent through the router.",
  ].join('\n')
}
